/**
 * "WITHDRAW THIS AMOUNT" — ONE RULE FOR THE FIGURE AND FOR THE FORM IT OPENS.
 * (ZEN-582)
 *
 * WHAT WENT WRONG. The Expenses page and the 529 withdrawal form each decided
 * for themselves which expenses count "through next month", by two different
 * rules. The page counted scheduled, not-yet-paid expenses (Round 24); the form
 * pre-ticked only expenses already paid, and ignored every filter on the page.
 * So the button beside "Awaiting · incl. next month $3,787.46" opened a form
 * filled with $2,914.22 — the $873.24 gap was the scheduled 1 October rent. Her
 * words: "the amount shown should be the amount next to the link."
 *
 * THE RULE NOW, stated once and asked by both screens:
 *
 *   1. The PAGE decides which expenses make up each figure — `awaitingFigures`
 *      below — from the rows it is showing, so every filter on screen counts.
 *   2. The page hands the form THAT LIST, not a description of it. The form
 *      does not work out its own set. (Owner ruling, 2026-10-04.)
 *   3. Each expense counts only what is still left on it — the part not already
 *      matched to an earlier withdrawal. (Owner ruling, 2026-10-04.)
 *   4. "Incl. next month" adds next month only when next month is in the same
 *      tax year. In December it adds nothing from January, and says so.
 *      (Owner ruling and approved sentence, 2026-10-04.)
 *   5. A button appears only when the form can fill in EXACTLY the figure
 *      beside it — `planFill` below, asked of the server's own answer for a
 *      withdrawal taken today. The figure stays either way. (Owner ruling,
 *      2026-10-04.)
 *
 * WHAT MAY BE MATCHED IS STILL THE SERVER'S ANSWER, NOT THIS FILE'S. `planFill`
 * reads the eligible-expense list the withdrawal form itself loads, through the
 * shared headroom rules (withdrawalHeadroom.js). Nothing here re-derives the
 * same-calendar-year rule, the beneficiary rule or any other backing rule.
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { localISO } from "@/lib/format";
import { pricedSum, isPricedRow, isNotYetPaid } from "@/lib/expenseTabs";
import { newMoneyUsd, TIE_MIN_USD } from "@/lib/withdrawalHeadroom";

const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;

/** The status the two "Withdraw this amount" figures are about. */
export const AWAITING_STATUS = "confirmed";

/** The grey notes beside "incl. next month". The December one is her approved
 *  wording, 2026-10-04 — change it only with her approval. */
export const NEXT_MONTH_NOTE = "adds next month's recurring due";
export const DECEMBER_NOTE = "January starts a new tax year, so it is not added here.";

/** Shown on the withdrawal form when it was opened from a figure it can no
 *  longer fill in exactly — the expenses changed between the click and the form
 *  loading. Nothing is ticked in that case.
 *  Her approved wording, 2026-10-04 — change it only with her approval. */
export const FIGURE_CHANGED_NOTE = "The expenses behind that figure changed after the Expenses page loaded, so nothing has been filled in. Go back to Expenses to see the current figure.";

/**
 * HOW FAR "INCL. NEXT MONTH" REACHES, from the viewer's own day.
 *
 * `endISO` is EXCLUSIVE: a row counts when its date is before it. Ordinarily
 * that is the first day of the month after next. In December, next month is
 * January — a different tax year, which a withdrawal taken now can never cover
 * (ZEN-158) — so the window stops at the end of December and `addsNextMonth`
 * is false, which is what tells the page to show DECEMBER_NOTE.
 */
export function nextMonthWindow(todayISO) {
  const y = Number(String(todayISO).slice(0, 4));
  const m = Number(String(todayISO).slice(5, 7)); // 1..12
  if (m === 12) return { endISO: `${y + 1}-01-01`, addsNextMonth: false };
  const em = m + 2; // first day of the month after next, 1-based
  const endISO = em > 12
    ? `${y + 1}-${String(em - 12).padStart(2, "0")}-01`
    : `${y}-${String(em).padStart(2, "0")}-01`;
  return { endISO, addsNextMonth: true };
}

/**
 * WHAT ONE EXPENSE CONTRIBUTES TO A FIGURE. null when it cannot be priced —
 * left out and counted, never valued at zero (ZEN-632).
 *
 * For the AWAITING figures that is what is still left on it: the expense less
 * whatever earlier withdrawals already cover. Under any other status filter the
 * figure keeps reading the whole expense, as it always has — "Reconciled · to
 * date" is not a question about money still to come out of the plan.
 */
export function countedUsd(e, status) {
  if (!isPricedRow(e)) return null;
  if (status !== AWAITING_STATUS) return e.usd_amount;
  const left = round2(e.usd_amount - (Number(e.alloc_usd) || 0));
  return left > TIE_MIN_USD ? left : 0;
}

function figureOf(rows, status) {
  const counted = [];
  for (const e of rows) {
    const usd = countedUsd(e, status);
    if (usd == null || usd <= TIE_MIN_USD) continue;
    counted.push({ id: e.id, usd });
  }
  // Added through the same rule as every other total on the page.
  const total = round2(pricedSum(counted.map((c) => ({ usd_amount: c.usd }))).total);
  return { rows: counted, ids: counted.map((c) => c.id), total };
}

/**
 * THE TWO FIGURES, from the rows the page is showing.
 *
 *   toDate         this status, already paid.
 *   inclNextMonth  the same, plus anything not yet paid that falls inside the
 *                  window and has not been ruled out of the 529 — the Round 24
 *                  rule, unchanged except for the December stop.
 */
export function awaitingFigures(rows, { status, todayISO }) {
  const { endISO, addsNextMonth } = nextMonthWindow(todayISO);
  const list = rows || [];
  const toDate = list.filter((e) => e.status === status && !isNotYetPaid(e));
  const inclNextMonth = list.filter((e) => {
    const d = (e.transaction_date || "").slice(0, 10);
    if (!d || d >= endISO) return false;
    return isNotYetPaid(e) ? e.eligible_529 !== false : e.status === status;
  });
  return {
    toDate: figureOf(toDate, status),
    inclNextMonth: figureOf(inclNextMonth, status),
    addsNextMonth,
  };
}

/**
 * CAN THE FORM FILL IN EXACTLY THIS FIGURE, FROM EXACTLY THESE EXPENSES?
 *
 * `rows` is a figure's own list — each expense and what the figure counted for
 * it — so every expense is checked, not only the total.
 * `items` is the server's eligible-expense list for a withdrawal taken today —
 * the same list the form loads. Returns the selection to pre-tick, or null.
 *
 * Null when any expense is missing from that list, may not be matched today,
 * or would take a different amount than the figure counted for it — and when
 * the whole would not add up to `figure` to the cent. There is no "nearly":
 * filling in a different number than the one beside the button is the defect
 * this ticket removes.
 */
export function planFill(items, rows, figure) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const byId = new Map((items || []).filter(Boolean).map((it) => [it.id, it]));
  const amts = {};
  let total = 0;
  for (const { id, usd } of rows) {
    const take = newMoneyUsd(byId.get(id));
    if (take <= TIE_MIN_USD) return null;
    if (Math.abs(take - round2(usd)) > TIE_MIN_USD) return null;
    amts[id] = take;
    total = round2(total + take);
  }
  if (Math.abs(total - round2(figure)) > TIE_MIN_USD) return null;
  return { ids: rows.map((r) => r.id), amts, amount: total.toFixed(2) };
}

/** What the page hands the withdrawal page, in navigation state. */
export const NAV_STATE_KEY = "withdrawThisAmount";
export const withdrawNavState = (figure) => ({
  [NAV_STATE_KEY]: { rows: figure.rows, figure: figure.total },
});

/**
 * THE PAGE'S HALF, in one call: both figures, and whether each may carry a
 * button. Asks the server the same question the form will ask when it opens —
 * a withdrawal requested and dated today, for this one beneficiary.
 *
 * NO BENEFICIARY, NO BUTTONS. A withdrawal belongs to one child; with every
 * child on screen at once there is no form the button could fill in.
 */
export function useWithdrawThisAmount({ rows, status, studentId, enabled = true }) {
  const todayISO = localISO();
  const figures = awaitingFigures(rows, { status, todayISO });
  const asked = !!enabled && !!studentId && status === AWAITING_STATUS;
  // ONLY A FRESH ANSWER MAY SHOW A BUTTON. The key carries exactly what the
  // figures count, so any change on the page asks again; nothing is kept
  // between visits; and while an answer is being fetched, or the last attempt
  // failed, there is no button. "Cannot tell" is never "yes".
  const counted = [figures.toDate, figures.inclNextMonth]
    .map((fig) => fig.rows.map((r) => `${r.id}:${r.usd}`).join(",")).join("|");
  const { data, isSuccess, isFetching, isError } = useQuery({
    queryKey: ["wd-eligible-preview", studentId, todayISO, "withdraw-this-amount", counted],
    queryFn: async () => (await api.get(
      `/withdrawals/eligible-preview?student_id=${studentId}&as_of_date=${todayISO}&distribution_date=${todayISO}`)).data,
    enabled: asked,
    staleTime: 0, gcTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true,
  });
  const fresh = asked && isSuccess && !isFetching && !isError;
  const items = data?.items;
  const fills = (fig) => fresh && !!items && !!planFill(items, fig.rows, fig.total);
  return {
    ...figures,
    toDateButton: fills(figures.toDate),
    inclNextMonthButton: fills(figures.inclNextMonth),
  };
}
